import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import MainNavigation from '../components/common/MainNavigation';
import TeslaButton from '../components/common/TeslaButton';
import ScrollReveal from '../components/common/ScrollReveal';
import ProgressiveImage from '../components/common/ProgressiveImage';
import HeroImageCarousel from '../components/common/HeroImageCarousel';
import HeroFloatingGlassCard from '../components/common/HeroFloatingGlassCard';
import SiteFooter from '../components/common/SiteFooter';
import HomeDownloadSection from '../components/common/HomeDownloadSection';
import stylistService from '../services/stylistService';
import * as locationService from '../services/locationService';
import { Stylist } from '../types/stylist';
import { publicStylistRef } from '../utils/publicRefs';
import { useAuth } from '../context/AuthContext';
import StaticPageSeo from '../seo/StaticPageSeo';
// Removed tier imports - using commission-based model with optional Luxe Plan
import '../styles/MobileAppPromo.css';

// Helper function to generate initials from name
const getInitials = (name: string): string => {
  if (!name) return 'ST';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};


const NewHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [nearbyStylists, setNearbyStylists] = useState<Stylist[]>([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState<{ city: string; state: string } | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  // Fetch nearby stylists on mount
  useEffect(() => {
    fetchNearbyStylists();
  }, []);

  const fetchNearbyStylists = async () => {
    try {
      setLoading(true);
      
      // Try to get user's location
      let location = null;
      try {
        const locationDetails = await locationService.getFullLocationDetails();
        if (locationDetails) {
          location = {
            city: locationDetails.address?.city || '',
            state: locationDetails.address?.state || '',
            coordinates: locationDetails.coordinates
          };
          setUserLocation({ city: location.city, state: location.state });
        }
      } catch (error) {
        console.log('Could not get location, showing all stylists');
      }

      // Fetch stylists (nearby if location available)
      const params: any = { userType: 'stylist' };
      if (location && location.coordinates) {
        params.latitude = location.coordinates.latitude;
        params.longitude = location.coordinates.longitude;
        params.maxDistance = 25; // 25 miles radius
      }

      const stylists = await stylistService.getStylists(params);
      
      // Limit to 6 stylists for homepage
      setNearbyStylists(stylists.slice(0, 6));
    } catch (error) {
      console.error('Error fetching stylists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookStylist = (stylistId: string) => {
    // Guests browse the stylist's profile, services and availability first
    // (App Store 5.1.1(v)); sign-in is requested at the booking boundary.
    navigate(user ? `/client/stylist/${stylistId}` : `/browse/${stylistId}`);
  };

  return (
    <>
      {/* Platform-level Organization + WebSite JSON-LD lives on the homepage only. */}
      <StaticPageSeo path="/" includePlatformSchema />
      <MainNavigation variant="transparent" />
      
      <div className="scroll-snap-container">
        {/* Hero Section */}
        <HeroSection>
          <HeroBackground>
            <HeroImageCarousel />
          </HeroBackground>
          
          <HeroFloatingGlassCard>
            <HeroTitle>
              Discover Beauty
              <br />
              <span className="gradient-text">Near You</span>
            </HeroTitle>

            <HeroSubtitle>
              Book appointments with top-rated stylists in your area.
              <br />
              Trusted by thousands. Available whenever you need.
            </HeroSubtitle>

            <HeroActions>
              {!user ? (
                <>
                  <Link to="/register" style={{ textDecoration: 'none' }}>
                    <TeslaButton variant="primary" size="lg">
                      Get Started Free
                    </TeslaButton>
                  </Link>
                  <GlassSignInLink to="/login">Sign In</GlassSignInLink>
                </>
              ) : (
                <Link to="/client/browse" style={{ textDecoration: 'none' }}>
                  <TeslaButton variant="primary" size="lg">
                    Browse Stylists
                  </TeslaButton>
                </Link>
              )}
            </HeroActions>

            <StatsRow>
              <StatItem>
                <StatNumber>10K+</StatNumber>
                <StatLabel>Happy Clients</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>500+</StatNumber>
                <StatLabel>Expert Stylists</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>4.9★</StatNumber>
                <StatLabel>Average Rating</StatLabel>
              </StatItem>
            </StatsRow>
          </HeroFloatingGlassCard>
        </HeroSection>

        {/* Nearby Stylists Section */}
        <NearbyStylistsSection>
          <Container>
            <ScrollReveal animation="fade-in-up">
              <SectionHeader>
                <SectionSubtitle>DISCOVER</SectionSubtitle>
                <SectionTitle>
                  {userLocation 
                    ? `Top Stylists in ${userLocation.city}, ${userLocation.state}` 
                    : 'Featured Stylists'}
                </SectionTitle>
                <SectionDescription>
                  Book with confidence. All our stylists are verified professionals with exceptional reviews.
                </SectionDescription>
              </SectionHeader>
            </ScrollReveal>

            {loading ? (
              <StylistGrid>
                {Array.from({ length: 6 }).map((_, index) => (
                  <SkeletonCard key={index}>
                    <SkeletonImage />
                    <SkeletonContent>
                      <SkeletonLine width="70%" />
                      <SkeletonLine width="50%" />
                      <SkeletonLine width="60%" />
                      <SkeletonButton />
                    </SkeletonContent>
                  </SkeletonCard>
                ))}
              </StylistGrid>
            ) : nearbyStylists.length > 0 ? (
              <>
                <StylistGrid>
                  {nearbyStylists.map((stylist, index) => (
                    <ScrollReveal 
                      key={stylist._id} 
                      animation="fade-in-up" 
                      delay={100 * index}
                    >
                      <StylistCardNew 
                        onClick={() => handleBookStylist(publicStylistRef(stylist))}
                      >
                        <CardImage>
                          {(() => {
                            const profilePhotoUrl = stylist.profilePhoto?.url;
                            const hasError = imageErrors[stylist._id];
                            
                            if (profilePhotoUrl && !hasError) {
                              return (
                                <ProgressiveImage
                                  src={profilePhotoUrl}
                                  placeholderSrc={profilePhotoUrl}
                                  alt={stylist.name}
                                  aspectRatio="1:1"
                                  onError={() => {
                                    setImageErrors(prev => ({ ...prev, [stylist._id]: true }));
                                  }}
                                />
                              );
                            }
                            
                            return (
                              <InitialsAvatar>
                                <AvatarContent>
                                  <AvatarIconWrapper>
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                    </svg>
                                  </AvatarIconWrapper>
                                  <AvatarInitials>{getInitials(stylist.name || 'Stylist')}</AvatarInitials>
                                </AvatarContent>
                              </InitialsAvatar>
                            );
                          })()}
                          {stylist.providesMobileService && (
                            <MobileBadge>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99z"/>
                              </svg>
                              Mobile
                            </MobileBadge>
                          )}
                        </CardImage>
                        
                        <CardContent>
                          <CardHeader>
                            <CardNameWrapper>
                              <CardName>{stylist.name}</CardName>
                              {stylist.location && (
                                <CardLocation>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                  </svg>
                                  {stylist.location.city}, {stylist.location.state}
                                </CardLocation>
                              )}
                            </CardNameWrapper>
                            {(((stylist as any).metrics?.ratings?.average || stylist.ratings?.average || 0)) > 0 && (
                              <RatingBadge>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                </svg>
                                <RatingValue>{((stylist as any).metrics?.ratings?.average || stylist.ratings?.average || 0).toFixed(1)}</RatingValue>
                              </RatingBadge>
                            )}
                          </CardHeader>
                          
                          <CardRating>
                            <Stars>
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} active={i < Math.floor((stylist as any).metrics?.ratings?.average || stylist.ratings?.average || 0)}>★</Star>
                              ))}
                            </Stars>
                            <RatingCount>{(stylist as any).metrics?.ratings?.count || stylist.ratings?.count || 0} reviews</RatingCount>
                          </CardRating>
                          
                          {stylist.specialties && stylist.specialties.length > 0 && (
                            <CardTags>
                              {stylist.specialties.slice(0, 2).map((specialty, idx) => (
                                <Tag key={idx}>{specialty}</Tag>
                              ))}
                              {stylist.specialties.length > 2 && (
                                <Tag>+{stylist.specialties.length - 2}</Tag>
                              )}
                            </CardTags>
                          )}
                          
                          <CardButton>
                            <TeslaButton variant="primary" size="sm">
                              {user ? 'View Profile' : 'Book Now'}
                            </TeslaButton>
                          </CardButton>
                        </CardContent>
                      </StylistCardNew>
                    </ScrollReveal>
                  ))}
                </StylistGrid>

                <ViewAllSection>
                  <Link to={user ? "/client/browse" : "/browse"} style={{ textDecoration: 'none' }}>
                    <TeslaButton variant="outline" size="lg">
                      {'View All Stylists'}
                    </TeslaButton>
                  </Link>
                </ViewAllSection>
              </>
            ) : (
              <EmptyState>
                <EmptyIcon>🔍</EmptyIcon>
                <EmptyTitle>No stylists found in your area</EmptyTitle>
                <EmptyText>Try adjusting your location settings or check back soon</EmptyText>
              </EmptyState>
            )}
          </Container>
        </NearbyStylistsSection>

        {/* Features Section */}
        <FeaturesSection>
          <Container>
            <ScrollReveal animation="fade-in-up">
              <SectionHeader>
                <SectionSubtitle>WHY LOOKBEL</SectionSubtitle>
                <SectionTitle>The Modern Way to Book Beauty</SectionTitle>
              </SectionHeader>
            </ScrollReveal>

            <WhyLookBelGrid>
              <ScrollReveal animation="fade-in-up" delay={100}>
                <WhyLookBelCard>
                  <FeatureIcon>⚡</FeatureIcon>
                  <FeatureTitle>Instant Booking</FeatureTitle>
                  <FeatureText>
                    Book your appointment in seconds. No phone calls, no waiting.
                  </FeatureText>
                </WhyLookBelCard>
              </ScrollReveal>

              <ScrollReveal animation="fade-in-up" delay={200}>
                <WhyLookBelCard>
                  <FeatureIcon>✓</FeatureIcon>
                  <FeatureTitle>Verified Professionals</FeatureTitle>
                  <FeatureText>
                    All stylists are verified and background-checked for your safety.
                  </FeatureText>
                </WhyLookBelCard>
              </ScrollReveal>

              <ScrollReveal animation="fade-in-up" delay={300}>
                <WhyLookBelCard>
                  <FeatureIcon>💳</FeatureIcon>
                  <FeatureTitle>Secure Payments</FeatureTitle>
                  <FeatureText>
                    Pay securely online with our encrypted payment system.
                  </FeatureText>
                </WhyLookBelCard>
              </ScrollReveal>

              <ScrollReveal animation="fade-in-up" delay={400}>
                <WhyLookBelCard>
                  <FeatureIcon>🚗</FeatureIcon>
                  <FeatureTitle>Mobile Services</FeatureTitle>
                  <FeatureText>
                    Many stylists offer mobile services - beauty comes to you!
                  </FeatureText>
                </WhyLookBelCard>
              </ScrollReveal>

              <ScrollReveal animation="fade-in-up" delay={500}>
                <WhyLookBelCard>
                  <FeatureIcon>📅</FeatureIcon>
                  <FeatureTitle>Easy Management</FeatureTitle>
                  <FeatureText>
                    Reschedule or cancel anytime. Manage all appointments in one place.
                  </FeatureText>
                </WhyLookBelCard>
              </ScrollReveal>

              <ScrollReveal animation="fade-in-up" delay={600}>
                <WhyLookBelCard>
                  <FeatureIcon>⭐</FeatureIcon>
                  <FeatureTitle>Reviewed & Rated</FeatureTitle>
                  <FeatureText>
                    Read authentic reviews from real clients before you book.
                  </FeatureText>
                </WhyLookBelCard>
              </ScrollReveal>
            </WhyLookBelGrid>
          </Container>
        </FeaturesSection>

        {/* Referral Program Section */}
        <AffiliateSection>
          <Container>
            <ScrollReveal animation="fade-in-up">
              <AffiliateContent>
                <AffiliateIcon>💎</AffiliateIcon>
                <AffiliateTitle>Join Our Referral Program</AffiliateTitle>
                <AffiliateText>
                  Earn money by sharing LookBel with your audience. Get unique referral links,
                  track your performance, and receive commissions on every successful booking.
                </AffiliateText>
                <AffiliateFeatures>
                  <AffiliateFeature>
                    <span>💰</span>
                    <span>Earn up to 15% commission</span>
                  </AffiliateFeature>
                  <AffiliateFeature>
                    <span>📊</span>
                    <span>Real-time analytics dashboard</span>
                  </AffiliateFeature>
                  <AffiliateFeature>
                    <span>🎁</span>
                    <span>Marketing materials provided</span>
                  </AffiliateFeature>
                  <AffiliateFeature>
                    <span>💳</span>
                    <span>Flexible payout options</span>
                  </AffiliateFeature>
                </AffiliateFeatures>
                <AffiliateActions>
                  <Link to="/affiliate/register" style={{ textDecoration: 'none' }}>
                    <TeslaButton variant="primary" size="lg">
                      Become a Referral Agent
                    </TeslaButton>
                  </Link>
                  <Link to="/affiliate/login" style={{ textDecoration: 'none' }}>
                    <TeslaButton variant="outline" size="lg">
                      Referral Agent Login
                    </TeslaButton>
                  </Link>
                </AffiliateActions>
              </AffiliateContent>
            </ScrollReveal>
          </Container>
        </AffiliateSection>

        {/* Stylist Payment Options Section */}
        <StylistPaymentSection>
          <Container>
            <ScrollReveal animation="fade-in-up">
              <SectionHeader>
                <SectionSubtitle>FOR STYLISTS</SectionSubtitle>
                <SectionTitle>Start Free, Grow Your Business</SectionTitle>
                <SectionDescription>
                  Join thousands of beauty professionals who trust LookBel to grow their business. 
                  No upfront costs, no monthly fees - just pure growth potential.
                </SectionDescription>
              </SectionHeader>
            </ScrollReveal>

            <HeroPaymentCard>
              <ScrollReveal animation="fade-in-up" delay={100}>
                <PaymentHeroSection>
                  <PaymentHeroIcon>🚀</PaymentHeroIcon>
                  <PaymentHeroTitle>Commission-Based</PaymentHeroTitle>
                  <PaymentHeroPrice>Free to Start</PaymentHeroPrice>
                  <PaymentHeroSubPrice>Pay only when you earn</PaymentHeroSubPrice>
                  <PaymentHeroDescription>
                    Launch your beauty business with zero risk. We only succeed when you succeed. 
                    LookBel brings you clients, you provide amazing service, and we share in your success.
                  </PaymentHeroDescription>
                </PaymentHeroSection>
              </ScrollReveal>

              <FeaturesGrid>
                <ScrollReveal animation="fade-in-up" delay={150}>
                  <FeatureCard>
                    <FeatureIconWrapper color="#10b981">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                      </svg>
                    </FeatureIconWrapper>
                    <FeatureContent>
                      <FeatureTitle>Zero Investment</FeatureTitle>
                      <FeatureDescription>Start earning today with no upfront costs</FeatureDescription>
                    </FeatureContent>
                  </FeatureCard>
                </ScrollReveal>

                <ScrollReveal animation="fade-in-up" delay={200}>
                  <FeatureCard>
                    <FeatureIconWrapper color="#C9A84C">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </FeatureIconWrapper>
                    <FeatureContent>
                      <FeatureTitle>Smart Booking</FeatureTitle>
                      <FeatureDescription>Professional appointment management system</FeatureDescription>
                    </FeatureContent>
                  </FeatureCard>
                </ScrollReveal>

                <ScrollReveal animation="fade-in-up" delay={250}>
                  <FeatureCard>
                    <FeatureIconWrapper color="#8b5cf6">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                        <line x1="1" y1="10" x2="23" y2="10" />
                      </svg>
                    </FeatureIconWrapper>
                    <FeatureContent>
                      <FeatureTitle>Auto Payments</FeatureTitle>
                      <FeatureDescription>Secure processing & digital wallet included</FeatureDescription>
                    </FeatureContent>
                  </FeatureCard>
                </ScrollReveal>

                <ScrollReveal animation="fade-in-up" delay={300}>
                  <FeatureCard>
                    <FeatureIconWrapper color="#f59e0b">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    </FeatureIconWrapper>
                    <FeatureContent>
                      <FeatureTitle>Live Chat</FeatureTitle>
                      <FeatureDescription>Real-time messaging with clients & support</FeatureDescription>
                    </FeatureContent>
                  </FeatureCard>
                </ScrollReveal>

                <ScrollReveal animation="fade-in-up" delay={350}>
                  <FeatureCard>
                    <FeatureIconWrapper color="#ec4899">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                        <polyline points="17 6 23 6 23 12" />
                      </svg>
                    </FeatureIconWrapper>
                    <FeatureContent>
                      <FeatureTitle>Analytics & Insights</FeatureTitle>
                      <FeatureDescription>Market predictions & revenue forecasting</FeatureDescription>
                    </FeatureContent>
                  </FeatureCard>
                </ScrollReveal>

                <ScrollReveal animation="fade-in-up" delay={400}>
                  <FeatureCard>
                    <FeatureIconWrapper color="#06b6d4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                    </FeatureIconWrapper>
                    <FeatureContent>
                      <FeatureTitle>Salon Management</FeatureTitle>
                      <FeatureDescription>Invite stylists & manage your team</FeatureDescription>
                    </FeatureContent>
                  </FeatureCard>
                </ScrollReveal>

                <ScrollReveal animation="fade-in-up" delay={450}>
                  <FeatureCard>
                    <FeatureIconWrapper color="#C0392B">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                      </svg>
                    </FeatureIconWrapper>
                    <FeatureContent>
                      <FeatureTitle>Loyalty Rewards</FeatureTitle>
                      <FeatureDescription>Built-in program to retain your clients</FeatureDescription>
                    </FeatureContent>
                  </FeatureCard>
                </ScrollReveal>

                <ScrollReveal animation="fade-in-up" delay={500}>
                  <FeatureCard>
                    <FeatureIconWrapper color="#14b8a6">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </FeatureIconWrapper>
                    <FeatureContent>
                      <FeatureTitle>Easy Rescheduling</FeatureTitle>
                      <FeatureDescription>Flexible booking changes & cancellations</FeatureDescription>
                    </FeatureContent>
                  </FeatureCard>
                </ScrollReveal>

                <ScrollReveal animation="fade-in-up" delay={550}>
                  <FeatureCard>
                    <FeatureIconWrapper color="#f97316">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                      </svg>
                    </FeatureIconWrapper>
                    <FeatureContent>
                      <FeatureTitle>Portfolio Showcase</FeatureTitle>
                      <FeatureDescription>Display your best work & build credibility</FeatureDescription>
                    </FeatureContent>
                  </FeatureCard>
                </ScrollReveal>

                <ScrollReveal animation="fade-in-up" delay={600}>
                  <FeatureCard>
                    <FeatureIconWrapper color="#a855f7">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </FeatureIconWrapper>
                    <FeatureContent>
                      <FeatureTitle>Review Management</FeatureTitle>
                      <FeatureDescription>Collect & respond to client feedback</FeatureDescription>
                    </FeatureContent>
                  </FeatureCard>
                </ScrollReveal>

                <ScrollReveal animation="fade-in-up" delay={650}>
                  <FeatureCard>
                    <FeatureIconWrapper color="#84cc16">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </FeatureIconWrapper>
                    <FeatureContent>
                      <FeatureTitle>No Monthly Fees</FeatureTitle>
                      <FeatureDescription>Pay only when you get bookings</FeatureDescription>
                    </FeatureContent>
                  </FeatureCard>
                </ScrollReveal>

                <ScrollReveal animation="fade-in-up" delay={700}>
                  <FeatureCard>
                    <FeatureIconWrapper color="#6366f1">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
                        <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
                        <line x1="6" y1="1" x2="6" y2="4" />
                        <line x1="10" y1="1" x2="10" y2="4" />
                        <line x1="14" y1="1" x2="14" y2="4" />
                      </svg>
                    </FeatureIconWrapper>
                    <FeatureContent>
                      <FeatureTitle>Marketing Support</FeatureTitle>
                      <FeatureDescription>Promotion tools to grow your business</FeatureDescription>
                    </FeatureContent>
                  </FeatureCard>
                </ScrollReveal>
              </FeaturesGrid>

              <CTAFooter>
                <Link to="/register?userType=stylist" style={{ textDecoration: 'none', width: '100%', maxWidth: '400px' }}>
                  <TeslaButton variant="primary" fullWidth size="lg">
                    Start Growing Today - It's Free! 🚀
                  </TeslaButton>
                </Link>
                <CTANote>
                  No credit card required • Cancel anytime • Join in under 5 minutes
                </CTANote>
              </CTAFooter>
            </HeroPaymentCard>
          </Container>
        </StylistPaymentSection>

        {/* For Stylists Section */}
        <StylistsCTASection>
          <Container>
            <ScrollReveal animation="fade-in-up">
              <CTAContent>
                <CTATitle>Are You a Beauty Professional?</CTATitle>
                <CTAText>
                  Join LookBel and grow your business with powerful tools designed for modern stylists.
                  Manage bookings, payments, and clients all in one place.
                </CTAText>
                <Link to="/register?userType=stylist" style={{ textDecoration: 'none' }}>
                  <TeslaButton variant="primary" size="lg">
                    Join as a Stylist
                  </TeslaButton>
                </Link>
              </CTAContent>
            </ScrollReveal>
          </Container>
        </StylistsCTASection>

        {/* Final CTA Section */}
        <FinalCTASection>
          <Container>
            <ScrollReveal animation="fade-in-up">
              <FinalCTAContent>
                <FinalCTATitle>Ready to Get Started?</FinalCTATitle>
                <FinalCTAText>
                  Join thousands of satisfied clients and discover your next favorite stylist today.
                </FinalCTAText>
                <FinalCTAActions>
                  {!user ? (
                    <>
                      <Link to="/register" style={{ textDecoration: 'none' }}>
                        <TeslaButton variant="primary" size="lg">
                          Sign Up Free
                        </TeslaButton>
                      </Link>
                      <Link to="/login" style={{ textDecoration: 'none' }}>
                        <TeslaButton variant="outline" size="lg">
                          Sign In
                        </TeslaButton>
                      </Link>
                    </>
                  ) : (
                    <Link to="/client/browse" style={{ textDecoration: 'none' }}>
                      <TeslaButton variant="primary" size="lg">
                        Start Browsing
                      </TeslaButton>
                    </Link>
                  )}
                </FinalCTAActions>
              </FinalCTAContent>
            </ScrollReveal>
          </Container>
        </FinalCTASection>

        <HomeDownloadSection />
        <SiteFooter />
      </div>
    </>
  );
};

// Styled Components
const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const HeroTitle = styled.h1`
  position: relative;
  z-index: 1;
  font-size: clamp(36px, 6vw, 56px);
  font-weight: 700;
  margin: 0 0 16px;
  line-height: 1.12;
  text-shadow: 0 2px 20px rgba(15, 23, 42, 0.45);

  .gradient-text {
    background: linear-gradient(135deg, #ffffff 0%, #f3efff 55%, #e2d4ff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    filter: drop-shadow(0 2px 12px rgba(15, 23, 42, 0.35));
  }
`;

const HeroSubtitle = styled.p`
  position: relative;
  z-index: 1;
  font-size: clamp(15px, 2.2vw, 18px);
  margin: 0 0 28px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.94);
  text-shadow: 0 1px 14px rgba(15, 23, 42, 0.4);
`;

const HeroActions = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  gap: 14px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 28px;
`;

const GlassSignInLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 22px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-decoration: none;
  color: #fff;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.22);
    border-color: rgba(255, 255, 255, 0.65);
    transform: translateY(-1px);
  }
`;

const StatsRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  gap: clamp(24px, 5vw, 48px);
  flex-wrap: wrap;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.22);
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: clamp(26px, 4vw, 32px);
  font-weight: 700;
  margin-bottom: 4px;
  text-shadow: 0 2px 16px rgba(15, 23, 42, 0.4);
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  opacity: 0.88;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  text-shadow: 0 1px 10px rgba(15, 23, 42, 0.35);
`;

const NearbyStylistsSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%);
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionSubtitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #C9A84C;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 700;
  color: #171a20;
  margin-bottom: 16px;
`;

const SectionDescription = styled.p`
  font-size: 18px;
  color: #5c5e62;
  max-width: 600px;
  margin: 0 auto;
`;

const StylistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(420px, 1fr));
  gap: 24px;
  margin-bottom: 60px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const StylistCardNew = styled.div`
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 1.5px solid #e2e8f0;
  position: relative;
  display: flex;
  flex-direction: row;
  min-height: 180px;

  &:hover {
    transform: translateY(-6px);
    border-color: #cbd5e1;
    box-shadow: 
      0 16px 32px rgba(0, 0, 0, 0.08),
      0 0 0 1px rgba(0, 0, 0, 0.02);
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    min-height: auto;
  }
`;

const CardImage = styled.div`
  position: relative;
  width: 180px;
  min-width: 180px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  ${StylistCardNew}:hover & img {
    transform: scale(1.08);
  }
  
  @media (max-width: 480px) {
    width: 100%;
    min-width: 100%;
    height: 200px;
  }
`;

const InitialsAvatar = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%);
  position: relative;
  overflow: hidden;
  
  /* Subtle pattern */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(circle at 30% 40%, rgba(0, 0, 0, 0.02) 0%, transparent 40%),
      radial-gradient(circle at 70% 60%, rgba(0, 0, 0, 0.015) 0%, transparent 40%);
  }
`;

const AvatarContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const AvatarIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  svg {
    width: 28px;
    height: 28px;
    color: #94a3b8;
  }
`;

const AvatarInitials = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 1.5px;
  text-transform: uppercase;
`;

const MobileBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(255, 255, 255, 0.95);
  color: #1a1a1a;
  padding: 6px 10px;
  border-radius: 100px;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  letter-spacing: 0.2px;
  
  svg {
    color: #C9A84C;
  }
`;

const CardContent = styled.div`
  padding: 20px 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

const CardNameWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardName = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 2px 0;
  letter-spacing: -0.01em;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardRating = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
`;

const RatingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  background: #fefce8;
  border-radius: 6px;
  flex-shrink: 0;
  
  svg {
    width: 12px;
    height: 12px;
    color: #eab308;
  }
`;

const RatingValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #854d0e;
`;

const Stars = styled.div`
  display: flex;
  gap: 1px;
`;

const Star = styled.span<{ active: boolean }>`
  color: ${props => props.active ? '#eab308' : '#e2e8f0'};
  font-size: 12px;
`;

const RatingCount = styled.span`
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
`;

const CardLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
  
  svg {
    flex-shrink: 0;
    color: #94a3b8;
  }
`;

const CardTags = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 12px;
  margin-bottom: 20px;
`;

const Tag = styled.span`
  background: #f1f5f9;
  color: #475569;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 500;
  text-transform: capitalize;
  letter-spacing: 0.2px;
`;

const CardButton = styled.div`
  margin-top: auto;
  padding-top: 12px;
  
  button {
    padding: 10px 20px;
    border-radius: 10px;
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.2px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
      transform: translateY(-1px);
    }
  }
`;

const ViewAllSection = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #171a20;
  margin-bottom: 12px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: #5c5e62;
`;

const FeaturesSection = styled.section`
  padding: 100px 0;
  background: white;
`;

const WhyLookBelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-top: 60px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const WhyLookBelCard = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: white;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
`;

const FeatureText = styled.p`
  font-size: 16px;
  color: #5c5e62;
  line-height: 1.6;
`;

const AffiliateSection = styled.section`
  padding: 100px 0;
`;

const AffiliateContent = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const AffiliateIcon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
`;

const AffiliateTitle = styled.h2`
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 700;
  margin-bottom: 16px;
  color: #171a20;
`;

const AffiliateText = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 40px;
  color: #5c5e62;
`;

const AffiliateFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 40px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AffiliateFeature = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  background: #f8f9ff;
  border-radius: 12px;
  
  span:first-child {
    font-size: 32px;
  }
  
  span:last-child {
    font-size: 14px;
    font-weight: 500;
    color: #171a20;
  }
`;

const AffiliateActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StylistsCTASection = styled.section`
  padding: 100px 0;
`;

const CTAContent = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 700;
  margin-bottom: 24px;
  color: #171a20;
`;

const CTAText = styled.p`
  font-size: 20px;
  margin-bottom: 40px;
  color: #5c5e62;
  line-height: 1.6;
`;

const FinalCTASection = styled.section`
  padding: 100px 0;
  background: #f8f9ff;
`;

const FinalCTAContent = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
`;

const FinalCTATitle = styled.h2`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 700;
  color: #171a20;
  margin-bottom: 24px;
`;

const FinalCTAText = styled.p`
  font-size: 20px;
  color: #5c5e62;
  margin-bottom: 40px;
`;

const FinalCTAActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

// Skeleton components
const SkeletonCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const SkeletonImage = styled.div`
  width: 100%;
  height: 280px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonContent = styled.div`
  padding: 24px;
`;

const SkeletonLine = styled.div<{ width?: string }>`
  height: 16px;
  width: ${props => props.width || '100%'};
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 12px;
`;

const SkeletonButton = styled.div`
  height: 44px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin-top: 20px;
`;

// Stylist Payment Options Section Styles
const StylistPaymentSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(180deg, #ffffff 0%, #f8f9ff 100%);
`;














const HeroPaymentCard = styled.div`
  max-width: 1200px;
  margin: 60px auto 0;
`;

const PaymentHeroSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
  padding: 40px;
  background: linear-gradient(135deg, #C9A84C 0%, #A8872A 100%);
  border-radius: 24px;
  color: white;
  box-shadow: 0 20px 60px rgba(102, 126, 234, 0.4);
`;

const PaymentHeroIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
`;

const PaymentHeroTitle = styled.h2`
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 12px;
  color: white;
`;

const PaymentHeroPrice = styled.div`
  font-size: 48px;
  font-weight: 900;
  margin: 16px 0 8px;
  color: #fbbf24;
  text-shadow: 0 2px 10px rgba(251, 191, 36, 0.3);
`;

const PaymentHeroSubPrice = styled.div`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 24px;
  font-weight: 500;
`;

const PaymentHeroDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.95);
  max-width: 700px;
  margin: 0 auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FeatureCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f3f4f6;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
    border-color: #e5e7eb;
  }
`;

const FeatureIconWrapper = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 12px;
  background: ${props => `${props.color}15`};
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  ${FeatureCard}:hover & {
    transform: scale(1.1) rotate(5deg);
    background: ${props => props.color};
    color: white;
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #171a20;
  margin-bottom: 6px;
  line-height: 1.4;
`;

const FeatureDescription = styled.p`
  font-size: 14px;
  color: #5c5e62;
  line-height: 1.6;
  margin: 0;
`;

const CTAFooter = styled.div`
  text-align: center;
  padding: 40px;
  background: linear-gradient(135deg, #f8f9ff 0%, #fff 100%);
  border-radius: 24px;
  border: 2px dashed #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const CTANote = styled.p`
  font-size: 14px;
  color: #9ca3af;
  margin: 0;
  font-weight: 500;
`;


export default NewHomePage;

